import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toLightboxItems } from "../lib/asset-lightbox";
import type { Asset } from "../types";

/**
 * Состояние лайтбокса ассетов поверх текущей страницы: открыть на кликнутом ассете
 * (карусель листает весь набор), закрыть, открыть чат-источник текущего слайда.
 * `openSource(index)` получает индекс активного слайда от `MediaLightbox` (он листается
 * стрелками), резолвит `chatUuid` и навигирует — `GenerationJob`/S3 не трогаем.
 * Один общий инстанс на страницу; шарится между card/grid/table view.
 */
export function useAssetLightbox(assets: readonly Asset[]) {
    const navigate = useNavigate();
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const items = useMemo(() => toLightboxItems(assets), [assets]);

    const openAt = useCallback(
        (asset: Asset) => {
            const idx = assets.findIndex((a) => a.id === asset.id);
            setOpenIndex(idx >= 0 ? idx : 0);
        },
        [assets],
    );

    const close = useCallback(() => setOpenIndex(null), []);

    const openSource = useCallback(
        (index: number) => {
            const uuid = assets[index]?.chatUuid;
            if (uuid) void navigate(`/chat/${uuid}`);
            setOpenIndex(null);
        },
        [assets, navigate],
    );

    return {
        items,
        isOpen: openIndex !== null,
        initialIndex: openIndex ?? 0,
        openAt,
        close,
        openSource,
    };
}
